import React, { useState } from 'react';
import { BiFilterAlt, BiX } from 'react-icons/bi';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import '../../assets/css/FilterBox.css';
import apiClient from '../../services/apiClient';
import { useQuery } from '@tanstack/react-query';

const FilterBox = ({ isOpen, onClose, onFilter }) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [categorySearch, setCategorySearch] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');

  // React Query for keywords
  const { data: keywordsData = [] } = useQuery({
    queryKey: ['keywords'],
    queryFn: async () => {
      const response = await apiClient.get("/keywords");
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });

  // React Query for categories
  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await apiClient.get("/categories");
      return response.data?.data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });

  const removeFilter = (filter) => {
    setSelectedFilters(selectedFilters.filter(f => f !== filter));
  };

  const toggleCategory = (categoryName) => {
    if (selectedFilters.includes(categoryName)) {
      // Remove from selected filters
      setSelectedFilters(selectedFilters.filter(f => f !== categoryName));
    } else {
      // Add to selected filters
      setSelectedFilters([...selectedFilters, categoryName]);
    }
  };

  const toggleKeyword = (keyword) => {
    if (selectedFilters.includes(keyword)) {
      // Remove from selected filters
      setSelectedFilters(selectedFilters.filter(f => f !== keyword));
    } else {
      // Add to selected filters
      setSelectedFilters([...selectedFilters, keyword]);
    }
  };

  const clearAllCategories = () => {
    // Remove all category filters
    setSelectedFilters(selectedFilters.filter(filter => 
      !categoriesData.some(cat => cat.category_name === filter)
    ));
  };

  const clearAllKeywords = () => {
    // Remove all keyword filters
    setSelectedFilters(selectedFilters.filter(filter => 
      !keywordsData.some(kw => kw.keyword === filter)
    ));
  };

  const handlePriceChange = (index, value) => {
    const newValue = parseInt(value) || 0;
    const newRange = [...priceRange];

    if (index === 0) {
      newRange[0] = Math.min(newValue, priceRange[1]);
    } else {
      newRange[1] = Math.max(newValue, priceRange[0]);
    }

    newRange[0] = Math.max(0, Math.min(newRange[0], 10000));
    newRange[1] = Math.max(0, Math.min(newRange[1], 10000));

    setPriceRange(newRange);
  };

  const applyFilters = () => {
    const selectedCategories = categoriesData
      .filter(cat => selectedFilters.includes(cat.category_name))
      .map(cat => cat.id);
    
    const selectedKeywords = selectedFilters.filter(filter => 
      keywordsData.some(kw => kw.keyword === filter)
    );

    onFilter({
      categories: selectedCategories,
      keywords: selectedKeywords,
      priceRange: priceRange
    });
    onClose();
  };

  const clearFilters = () => {
    setSelectedFilters([]);
    setPriceRange([0, 10000]);
  };

  // Helper function to get checked status
  const getCategoryCheckedStatus = (categoryName) => {
    return selectedFilters.includes(categoryName);
  };

  const getKeywordCheckedStatus = (keyword) => {
    return selectedFilters.includes(keyword);
  };

  if (!isOpen) return null;

  return (
    <div className="FilterBoxOverlay">
      <div className="FilterBoxContainer">
        <div className="FilterBoxHeader">
          <h2>Filters</h2>
          <button onClick={onClose} className="closeBtn">
            <BiX />
          </button>
        </div>

        <div className="SelectedFilters">
          {selectedFilters.map(filter => (
            <div key={filter} className="SelectedFilterItem">
              {filter}
              <button onClick={() => removeFilter(filter)}>
                <BiX />
              </button>
            </div>
          ))}
        </div>

        <div className="FilterSection">
          <div 
            className="FilterSectionHeader" 
            onClick={() => setCategoriesOpen(!categoriesOpen)}
          >
            <p>Categories</p>
            {categoriesOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </div>
          
          {categoriesOpen && (
            <div className="FilterDropdown">
              <div className="FilterSearchBox">
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
              </div>
              
              <div className="FilterClearAll" onClick={clearAllCategories}>
                Clear all
              </div>
              
              <div className="FilterOptions">
                {categoriesData
                  .filter(cat => 
                    cat.category_name.toLowerCase().includes(categorySearch.toLowerCase())
                  )
                  .map(category => (
                    <label key={category.id} className="FilterOption">
                      <input
                        type="checkbox"
                        checked={getCategoryCheckedStatus(category.category_name)}
                        onChange={() => toggleCategory(category.category_name)}
                      />
                      <span>{category.category_name}</span>
                    </label>
                  ))}
                
                {categoriesData.filter(cat => 
                  cat.category_name.toLowerCase().includes(categorySearch.toLowerCase())
                ).length === 0 && (
                  <div className="NoResults">No categories found</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="FilterSection">
          <div 
            className="FilterSectionHeader" 
            onClick={() => setKeywordsOpen(!keywordsOpen)}
          >
            <p>Tags</p>
            {keywordsOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </div>
          
          {keywordsOpen && (
            <div className="FilterDropdown">
              <div className="FilterSearchBox">
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={keywordSearch}
                  onChange={(e) => setKeywordSearch(e.target.value)}
                />
              </div>
              
              <div className="FilterClearAll" onClick={clearAllKeywords}>
                Clear all
              </div>
              
              <div className="FilterOptions">
                {keywordsData
                  .filter(keyword => 
                    keyword.keyword.toLowerCase().includes(keywordSearch.toLowerCase())
                  )
                  .map((keyword, index) => (
                    <label key={index} htmlFor={`${index}-${keyword.keyword}`} className="FilterOption">
                      <input
                        type="checkbox"
                        checked={getKeywordCheckedStatus(keyword.keyword)}
                        onChange={() => toggleKeyword(keyword.keyword)}
                        id={`${index}-${keyword.keyword}`}
                      />
                      <span>{keyword.keyword}</span>
                    </label>
                  ))}
                
                {keywordsData.filter(keyword => 
                  keyword.keyword.toLowerCase().includes(keywordSearch.toLowerCase())
                ).length === 0 && (
                  <div className="NoResults">No tags found</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="FilterSection">
          <div className="FilterSectionHeader">
            <p>Price Range</p>
          </div>
          <div className="PriceRangeContainer">
            <div className="PriceInputs">
              <div className="PriceInput">
                <label>Min</label>
                <input
                  type="number"
                  value={priceRange[0]}
                  onChange={(e) => handlePriceChange(0, e.target.value)}
                />
              </div>
              
              <div className="PriceInput">
                <label>Max</label>
                <input
                  type="number"
                  value={priceRange[1]}
                  onChange={(e) => handlePriceChange(1, e.target.value)}
                />
              </div>
            </div>
            
            <div className="RangeSlider">
              <input
                type="range"
                min="0"
                max="10000"
                value={priceRange[0]}
                onChange={(e) => handlePriceChange(0, e.target.value)}
                className="PriceSlider"
              />
              <input
                type="range"
                min="0"
                max="10000"
                value={priceRange[1]}
                onChange={(e) => handlePriceChange(1, e.target.value)}
                className="PriceSlider"
              />
              <div 
                className="SliderTrack"
                style={{
                  width: `calc(100% * (${priceRange[1]} - ${priceRange[0]}) / 10000)`,
                  left: `calc(100% * ${priceRange[0]} / 10000)`
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="FilterActions">
          <button onClick={clearFilters} className="ClearButton">
            Clear Filters
          </button>
          <button onClick={applyFilters} className="ApplyButton">
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBox;